import { HStack, Button, Stack, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import { BrandCountrySelection } from '$/pages/AdminPages/components/BrandCountrySelection';
import { useAdminUserStore } from '$/pages/AdminPages/pages/UserPage/stores/useAdminUserStore';
import { queryClient } from '$/services/fetcher';
import { brandCountriesQuery } from '$/services/usecases/admin/brandCountries';
import {
  countryAdminsQuery,
  updateCountryAdmin,
} from '$/services/usecases/admin/countryAdmins';
import { BrandCountry } from '$/services/usecases/admin/mapper/brandCountries';

type AddCountryAdminData = {
  managedCountry: string;
  managedCountries: string[];
  isActiveAdmin: boolean;
};

export const EditCountryAdminForm = () => {
  const { t } = useTranslation();
  const initialRender = useRef(true);

  const onCloseModal = useAdminUserStore.useOnEditModalClose();
  const activeUser = useAdminUserStore.useActiveEditUser()!;
  const setModalType = useAdminUserStore.useSetEditModalType();
  const [selectedBrandCountries, setSelectedBrandCountries] = useState<
    BrandCountry[]
  >([]);

  const formMethods = useForm<AddCountryAdminData>({
    defaultValues: {
      isActiveAdmin: activeUser.isActiveAdmin,
      managedCountries: activeUser.managedCountries,
    },
  });

  const showToast = useToast();
  const { cloudMessageTranslation } = useCloudMessageTranslation();

  const [updateLoading, setUpdateLoading] = useState(false);

  const { data } = useQuery(brandCountriesQuery);
  const countries = data?.response.payload;

  useEffect(() => {
    const timeout = setTimeout(() => {
      initialRender.current = false;
    }, 50);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (countries == null) return;
    setSelectedBrandCountries(
      countries.filter((item) => activeUser.managedCountries.includes(item.id)),
    );
  }, [activeUser.managedCountries, countries]);

  useEffect(() => {
    if (initialRender.current) return;
    formMethods.setValue(
      'managedCountries',
      selectedBrandCountries.map((item) => item.id),
      {
        shouldDirty: true,
      },
    );
  }, [formMethods, selectedBrandCountries]);

  const onChangeCountries: Dispatch<SetStateAction<BrandCountry[]>> = (
    state,
  ) => {
    setSelectedBrandCountries(state);
  };

  const onSubmit = async (data: AddCountryAdminData) => {
    setUpdateLoading(true);

    const res = await updateCountryAdmin({
      id: activeUser.id,
      managedCountries: data.managedCountries,
    }).finally(() => setUpdateLoading(false));

    if (res.isSuccessful) {
      await queryClient.invalidateQueries({
        queryKey: countryAdminsQuery.queryKey,
      });

      return onCloseModal();
    }

    showToast(
      t('admin.userManagement.addUser.generalError'),
      'error',
      cloudMessageTranslation(res.response.message),
    );
  };

  return (
    <FormProvider {...formMethods}>
      <Stack as='form' gap='6' onSubmit={formMethods.handleSubmit(onSubmit)}>
        <Stack gap='1'>
          <Text color='lighterText'>{activeUser.email}</Text>
          <Text fontSize='2xl' fontWeight='bold'>
            {activeUser.firstName} {activeUser.lastName}
          </Text>
        </Stack>

        <BrandCountrySelection
          selectedBrandCountries={selectedBrandCountries}
          setSelectedBrandCountries={onChangeCountries}
          allBrandCountries={countries ?? []}
        />

        <HStack justifyContent='space-between' w='full' mt='4'>
          <Button
            color='lighterText'
            fontSize='sm'
            isDisabled={!formMethods.formState.isDirty}
            isLoading={updateLoading}
            type='submit'
            variant='ghost'
          >
            {t('admin.userManagement.editUser.save')}
          </Button>
          <Button
            px='6'
            py='3'
            fontSize='sm'
            onClick={() => setModalType('delete_admin')}
            variant='danger'
          >
            {t('admin.userManagement.editUser.delete')}
          </Button>
        </HStack>
      </Stack>
    </FormProvider>
  );
};
